// ============================================================
//  src/hooks/useCart.js
//
//  Replaces plain useState for cart in App.jsx.
//  Persists cart to localStorage so a page refresh doesn't
//  empty the basket.
//
//  Usage (in App.jsx):
//    import useCart from "./hooks/useCart";
//    // replace: const [cart, setCart] = useState([]);
//    const [cart, setCart] = useCart();
// ============================================================

import { useState, useEffect, useRef } from "react";

const STORAGE_KEY = "rk_cart_v1";

function readCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function useCart() {
  const [cart, setCartState] = useState(() => readCart());

  // ── Track whether this is the initial mount ───────────────
  // Don't write on the very first render (we just read from storage).
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch (err) {
      // Storage quota exceeded — fail silently, cart still works in memory
      console.warn("useCart: localStorage write failed", err);
    }
  }, [cart]);

  return [cart, setCartState];
}
